/*
 * /sonar-depth-map — the page: files in, map on screen, files out.
 *
 * Reading and gridding run in worker.js; this file owns the files list, the
 * settings, the Leaflet map (depth picture, isobaths with labels, passes,
 * holes and humps), the 3D view and the exports. Exports are the counted
 * unit (config/tiers.php, as /gpx-viewer): one map — one key, so saving the
 * same map again today, in any format, is free.
 */
(function () {
    'use strict';
    var SLUG = 'sonar-depth-map';
    var CFG = window.SDM_CFG || {};
    var RTL = CFG.dir === 'rtl';
    var STR = {};
    try { STR = JSON.parse(document.getElementById('sdmStrings').textContent) || {}; } catch (_) {}

    function $(id) { return document.getElementById(id); }
    function t(k, vars) {
        var s = STR[k] != null ? String(STR[k]) : k;
        if (vars) Object.keys(vars).forEach(function (v) { s = s.split(':' + v).join(vars[v]); });
        return s;
    }
    function ae(ev, data) { try { if (window.__ae) window.__ae(ev, data || {}); } catch (_) {} }
    function stage(s, d) { try { if (window.__stage) window.__stage(s, d); } catch (_) {} }
    function toast(msg, type) { try { if (window.__toast) window.__toast(msg, type || 'info'); } catch (_) {} }
    function load(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }
    function save(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
    function intlLocale() { try { return window.__intlLocale ? window.__intlLocale() : (CFG.locale || 'en'); } catch (_) { return 'en'; } }

    // ── units and numbers ──────────────────────────────────────────────────
    var units = load('units', CFG.locale === 'en' ? 'ft' : 'm') === 'ft' ? 'ft' : 'm';
    var nf = {};
    function fmtN(v, dec) {
        var k = dec;
        if (!nf[k]) { try { nf[k] = new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: dec, minimumFractionDigits: dec }); } catch (_) { nf[k] = { format: function (x) { return x.toFixed(dec); } }; } }
        return nf[k].format(v);
    }
    function depthTxt(m, dec) {
        if (!(m === m)) return '—';
        var v = units === 'ft' ? m / 0.3048 : m;
        if (dec == null) dec = v < 10 ? 1 : 0;
        return fmtN(v, dec) + ' ' + t(units === 'ft' ? 'u_ft' : 'u_m');
    }
    function levelTxt(m) {
        var v = units === 'ft' ? m / 0.3048 : m;
        var dec = Math.abs(v - Math.round(v)) < 1e-6 ? 0 : (Math.abs(v * 2 - Math.round(v * 2)) < 1e-6 ? 1 : 2);
        return fmtN(v, dec) + ' ' + t(units === 'ft' ? 'u_ft' : 'u_m');
    }
    function lengthTxt(m) {
        if (units === 'ft') { var mi = m / 1609.344; return mi >= 0.5 ? fmtN(mi, mi < 10 ? 1 : 0) + ' ' + t('u_mi') : fmtN(m / 0.3048, 0) + ' ' + t('u_ft'); }
        return m >= 1000 ? fmtN(m / 1000, m < 10000 ? 1 : 0) + ' ' + t('u_km') : fmtN(m, m < 10 ? 1 : 0) + ' ' + t('u_m');
    }
    function areaTxt(m2) {
        if (units === 'ft') { var ac = m2 / 4046.856; return fmtN(ac, ac < 10 ? 1 : 0) + ' ' + t('u_acre'); }
        var ha = m2 / 10000;
        return ha >= 1 ? fmtN(ha, ha < 10 ? 1 : 0) + ' ' + t('u_ha') : fmtN(m2, 0) + ' ' + t('u_m2');
    }
    function volumeTxt(m3) {
        if (units === 'ft') { var af = m3 / 1233.48; return fmtN(af, af < 10 ? 1 : 0) + ' ' + t('u_acft'); }
        return m3 >= 1e6 ? fmtN(m3 / 1e6, 2) + ' ' + t('u_mln_m3') : fmtN(m3, 0) + ' ' + t('u_m3');
    }

    // ── settings ───────────────────────────────────────────────────────────
    var opts = {
        interval: Number(load('interval', 0)) || 0,
        smooth: Number(load('smooth', 1)),
        maxEdge: Number(load('gaps', 60)),
        transducer: Number(load('transducer', 0)) || 0
    };
    if ([0, 1, 2].indexOf(opts.smooth) < 0) opts.smooth = 1;
    if (!(opts.maxEdge >= 0)) opts.maxEdge = 60;
    var palette = load('palette', 'blue') === 'sonar' ? 'sonar' : 'blue';
    var layersOn = {
        depth: load('l_depth', true) !== false, contours: load('l_contours', true) !== false, labels: load('l_labels', true) !== false,
        track: load('l_track', true) !== false, dots: load('l_dots', false) === true, features: load('l_features', true) !== false,
        slope: load('l_slope', false) === true, sat: load('l_sat', false) === true
    };
    var view = 'map', exag = Number(load('exag', 6)) || 6;

    // ── worker ─────────────────────────────────────────────────────────────
    var worker = null, seq = 0, pending = {};
    function warmWorker() {
        if (worker) return worker;
        try {
            worker = new Worker(CFG.workerUrl);
            worker.onmessage = function (ev) {
                var m = ev.data || {}, p = pending[m.id];
                if (!p) return;
                if (m.kind === 'progress') { if (p.onProgress) p.onProgress(m.done, m.total); return; }
                if (m.kind === 'stage') { if (p.onStage) p.onStage(m.stage); return; }
                delete pending[m.id];
                if (m.kind === 'error') p.reject(m); else p.resolve(m);
            };
            worker.onerror = function (e) {
                ae('tool_diagnostic', { issue: 'sdm_worker_error', err: String(e && e.message || 'worker').slice(0, 80) });
                Object.keys(pending).forEach(function (k) { pending[k].reject({ code: 'worker_failed' }); delete pending[k]; });
                worker = null;
            };
        } catch (e) {
            worker = null;
            ae('tool_diagnostic', { issue: 'sdm_worker_create', err: String(e && e.message || e).slice(0, 80) });
        }
        return worker;
    }
    function call(msg, hooks) {
        var w = warmWorker();
        if (!w) return Promise.reject({ code: 'worker_failed' });
        var id = msg.id || ('m' + (++seq));
        msg.id = id;
        return new Promise(function (resolve, reject) {
            pending[id] = { resolve: resolve, reject: reject, onProgress: hooks && hooks.onProgress, onStage: hooks && hooks.onStage };
            w.postMessage(msg);
        });
    }

    // ── files ──────────────────────────────────────────────────────────────
    var files = [];    // {id, file, name, size, status, format, meta, n, dmin, dmax, preview, offset, enabled, key, err}
    var fseq = 0, readingQueue = Promise.resolve(), closeRun = null, sampleMode = false, sampleWanted = false;

    function fmtLabel(f) {
        var k = 'fmt_' + (f.format || 'x');
        return STR[k] ? t(k) : (f.format || '').toUpperCase();
    }
    function humBeam(name) { var m = /^B00([0-4])\.SON$/i.exec(name || ''); return m ? Number(m[1]) : -1; }

    // A Humminbird recording is a .DAT plus a folder of .SON/.IDX: the depth
    // comes from one down-looking beam, so of B000…B004 only the best one is
    // read (B001 = 200 kHz, B000 = 50/83 kHz, B004 = down imaging); side scan
    // (B002/B003) and the index files are skipped and said to be skipped.
    function selectFiles(list) {
        var son = list.filter(function (f) { return /\.son$/i.test(f.name); });
        var pickBeam = -1;
        [1, 0, 4].some(function (b) { if (son.some(function (f) { return humBeam(f.name) === b; })) { pickBeam = b; return true; } return false; });
        var keep = [], skipped = 0;
        list.forEach(function (f) {
            if (/\.(idx|dat)$/i.test(f.name)) { skipped++; return; }
            if (/\.son$/i.test(f.name) && pickBeam >= 0 && humBeam(f.name) !== pickBeam) { skipped++; return; }
            keep.push(f);
        });
        return { keep: keep, skipped: skipped };
    }

    function addFiles(list, mode, isSample) {
        list = Array.prototype.slice.call(list || []).filter(Boolean);
        if (!list.length) return;
        if (mode !== 'add') clearAll(true);
        sampleMode = !!isSample || (mode === 'add' && sampleMode);
        $('sdmSampleNote').hidden = !sampleMode;
        var sel = selectFiles(list);
        if (!sel.keep.length) { showErrors([t('err_only_aux')]); return; }
        if (sel.skipped) toast(t('hum_skipped', { n: sel.skipped }), 'info');
        sel.keep.forEach(function (file) {
            var f = {
                id: 'f' + (++fseq), file: file, name: file.name || 'log', size: file.size || 0,
                status: 'queued', offset: 0, enabled: true,
                key: window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : (file.name + '|' + file.size)
            };
            files.push(f);
            readingQueue = readingQueue.then(function () { return readOne(f); });
        });
        showWork();
        renderFiles();
        readingQueue = readingQueue.then(function () { scheduleBuild(0); });
    }

    function readOne(f) {
        if (f.status !== 'queued') return Promise.resolve();
        f.status = 'reading';
        renderFiles();
        if (!closeRun && window.__runMark) closeRun = window.__runMark.begin();
        setBusy(true, t('busy_reading', { name: f.name }), 0);
        var t0 = Date.now();
        return call({ op: 'parse', id: f.id, file: f.file, name: f.name }, {
            onProgress: function (done, total) { setBusy(true, t('busy_reading', { name: f.name }), total ? done / total : 0); }
        }).then(function (m) {
            f.status = 'ready'; f.format = m.format; f.meta = m.meta; f.n = m.n; f.dmin = m.dmin; f.dmax = m.dmax; f.preview = m.preview;
            stage('file_read', { fmt: m.format });
            ae('feature_use', { slug: SLUG, f: 'sdm_read', fmt: m.format, n: m.n, mb: Math.round(f.size / 1048576), ms: Date.now() - t0, stale: Math.round((m.meta.repeatedFixShare || 0) * 100) });
        }, function (e) {
            f.status = 'error'; f.err = (e && e.code) || 'read_failed';
            ae('tool_diagnostic', { issue: 'sdm_read_failed', code: f.err, ext: (/\.([a-z0-9]+)$/i.exec(f.name) || [])[1] || '' });
            showErrors([errText(f.err, f.name)]);
        }).then(function () { renderFiles(); });
    }

    function errText(code, name) {
        var k = 'err_' + code;
        return STR[k] ? t(k, { name: name }) : t('err_read_failed', { name: name });
    }

    function removeFile(id) {
        var i = files.findIndex(function (f) { return f.id === id; });
        if (i < 0) return;
        if (worker) worker.postMessage({ op: 'drop', id: id });
        files.splice(i, 1);
        if (!files.length) { clearAll(); return; }
        renderFiles();
        scheduleBuild(0);
    }

    function clearAll(keepZone) {
        files.forEach(function (f) { if (worker) worker.postMessage({ op: 'drop', id: f.id }); });
        files = [];
        result = null;
        sampleMode = false;
        clearMap();
        if (view3d) { view3d.dispose(); view3d = null; }
        $('sdmWork').hidden = true;
        $('sdmSampleNote').hidden = true;
        hideErrors();
        setBusy(false);
        if (!keepZone) {
            var zone = $('sdmUpload');
            // silent: this page is already tearing its workspace down
            try { if (zone && zone.__uploaderRestore) zone.__uploaderRestore(true); } catch (_) {}
        }
    }

    // ── build ──────────────────────────────────────────────────────────────
    var result = null, buildTimer = 0, buildSeq = 0;
    function scheduleBuild(ms) {
        clearTimeout(buildTimer);
        buildTimer = setTimeout(build, ms == null ? 250 : ms);
    }
    function build() {
        var ready = files.filter(function (f) { return f.status === 'ready' && f.enabled; });
        if (!ready.length) {
            if (closeRun) { closeRun(); closeRun = null; }
            setBusy(false);
            if (!files.some(function (f) { return f.status === 'reading' || f.status === 'queued'; })) {
                if (files.length && files.every(function (f) { return f.status === 'error'; })) $('sdmWork').hidden = true;
            }
            return;
        }
        var my = ++buildSeq;
        setBusy(true, t('busy_building'), null);
        var o = {
            interval: opts.interval, unit: units, smooth: opts.smooth, maxEdge: opts.maxEdge > 0 ? opts.maxEdge : 1e9,
            offset: opts.transducer
        };
        var t0 = Date.now();
        call({ op: 'build', files: ready.map(function (f) { return { id: f.id, offset: -(f.offset || 0) }; }), opts: o }, {
            onStage: function (s) { if (my === buildSeq) setBusy(true, t('stage_' + s) || t('busy_building'), null); }
        }).then(function (m) {
            if (my !== buildSeq) return;
            var first = !result;
            result = m.result;
            setBusy(false);
            if (closeRun) { closeRun(); closeRun = null; }
            render(first);
            stage('map_built');
            ae('feature_use', { slug: SLUG, f: 'sdm_built', ms: Date.now() - t0, bins: result.stats.bins, cell: result.stats.cell, mae: result.accuracy ? Math.round(result.accuracy.mae * 100) : -1, files: ready.length });
        }, function (e) {
            if (my !== buildSeq) return;
            setBusy(false);
            if (closeRun) { closeRun(); closeRun = null; }
            ae('tool_diagnostic', { issue: 'sdm_build_failed', code: (e && e.code) || 'x', err: String(e && e.message || '').slice(0, 80) });
            showErrors([t((e && e.code) === 'too_few_points' || (e && e.code) === 'no_points' ? 'err_too_few' : 'err_build')]);
        });
    }

    // ── map ────────────────────────────────────────────────────────────────
    var map = null, baseLayer = null, satLayer = null, lyr = {}, labelMarkers = [], tip = null;
    function initMap() {
        if (map || typeof L === 'undefined') return;
        map = L.map($('sdmMap'), { zoomControl: false, attributionControl: true, preferCanvas: true, maxZoom: 21 }).setView([CFG.home[0], CFG.home[1]], CFG.home[2]);
        L.control.zoom({ position: RTL ? 'topleft' : 'topright' }).addTo(map);
        map.createPane('sdmDepth'); map.getPane('sdmDepth').style.zIndex = 350;
        map.createPane('sdmLines'); map.getPane('sdmLines').style.zIndex = 420;
        var tile = CFG.tile || {};
        if (window.__mapTiles) {
            baseLayer = window.__mapTiles.base(L, map, null, {
                slug: SLUG, styles: tile.styles, labels: tile.labels, control: false, opts: { maxZoom: 21 },
                onExhausted: function () { var o = $('sdmMapOffline'); if (o) { o.textContent = t('map_offline'); o.hidden = false; } }
            });
        } else if (window.__basemapFallback) {
            baseLayer = window.__basemapFallback(L, map, '');
        }
        if (typeof ResizeObserver !== 'undefined') new ResizeObserver(function () { if (map) map.invalidateSize({ pan: false }); }).observe($('sdmMap'));
        map.on('zoomend moveend', placeLabels);
        map.on('mousemove', function (e) { showTip(e.latlng, e.containerPoint); });
        map.on('mouseout', function () { if (tip) tip.hidden = true; });
        map.on('click', function (e) { showTip(e.latlng, e.containerPoint, true); });
        applySat();
    }
    function applySat() {
        if (!map) return;
        if (layersOn.sat && !sampleMode) {
            if (!satLayer && window.GpsTerrain && (CFG.tile || {}).sat) {
                satLayer = window.GpsTerrain.chain(L, map, CFG.tile.sat, { attribution: CFG.tile.satAttr, maxNativeZoom: 19, maxZoom: 21,
                    onExhausted: function () { toast(t('sat_failed'), 'warning'); layersOn.sat = false; save('l_sat', false); applySat(); syncLayerInputs(); } });
            }
            if (satLayer) {
                if (baseLayer && map.hasLayer(baseLayer)) { try { map.removeLayer(baseLayer); } catch (_) {} }
                if (!map.hasLayer(satLayer)) satLayer.addTo(map);
            }
        } else {
            if (satLayer && map.hasLayer(satLayer)) map.removeLayer(satLayer);
            if (baseLayer && !map.hasLayer(baseLayer)) { try { baseLayer.addTo(map); } catch (_) {} }
        }
    }
    function clearMap() {
        if (!map) return;
        Object.keys(lyr).forEach(function (k) { if (lyr[k]) { try { map.removeLayer(lyr[k]); } catch (_) {} } });
        lyr = {};
        labelMarkers.forEach(function (m) { try { map.removeLayer(m); } catch (_) {} });
        labelMarkers = [];
        if (overlayUrl) { try { URL.revokeObjectURL(overlayUrl); } catch (_) {} overlayUrl = null; }
    }

    var overlayUrl = null, slopeUrl = null;
    function canvasUrl(cv) {
        return canvasBlob(cv, 'image/png').then(function (b) { return URL.createObjectURL(b); }, function () { return cv.toDataURL('image/png'); });
    }

    function render(first) {
        initMap();
        if (!map || !result) return;
        var R = window.SDMRender, res = result;
        clearMap();
        var gb = R.bounds(res), bounds = L.latLngBounds([gb.south, gb.west], [gb.north, gb.east]);
        var my = buildSeq;
        if (layersOn.depth) {
            canvasUrl(R.overlay(res, { palette: palette })).then(function (u) {
                if (my !== buildSeq || !map) { if (u && u.indexOf('blob:') === 0) URL.revokeObjectURL(u); return; }
                overlayUrl = u.indexOf('blob:') === 0 ? u : null;
                lyr.depth = L.imageOverlay(u, bounds, { pane: 'sdmDepth', opacity: 0.88, interactive: false, className: 'sdm-depth-img' }).addTo(map);
            });
        }
        if (layersOn.slope) {
            canvasUrl(R.slopeOverlay(res)).then(function (u) {
                if (my !== buildSeq || !map) return;
                if (slopeUrl) { try { URL.revokeObjectURL(slopeUrl); } catch (_) {} }
                slopeUrl = u.indexOf('blob:') === 0 ? u : null;
                lyr.slope = L.imageOverlay(u, bounds, { pane: 'sdmDepth', opacity: 0.85, interactive: false, className: 'sdm-depth-img' }).addTo(map);
            });
        }
        if (layersOn.track) {
            var tracks = L.layerGroup();
            files.forEach(function (f) {
                if (f.status !== 'ready' || !f.enabled || !f.preview) return;
                var p = f.preview, pts = [], seg = [];
                for (var i = 0; i < p.n; i++) {
                    if (i && Math.abs(p.lat[i] - p.lat[i - 1]) + Math.abs(p.lon[i] - p.lon[i - 1]) > 0.002) { if (seg.length > 1) pts.push(seg); seg = []; }
                    seg.push([p.lat[i], p.lon[i]]);
                }
                if (seg.length > 1) pts.push(seg);
                L.polyline(pts, { pane: 'sdmLines', color: '#ff7a1a', weight: 1.4, opacity: 0.75, interactive: false }).addTo(tracks);
            });
            lyr.track = tracks.addTo(map);
        }
        if (layersOn.dots) {
            var dots = L.layerGroup(), s = res.soundings, step = Math.max(1, Math.ceil(s.n / 12000)), pal = R.palette(palette), rg = R.range(res);
            for (var i = 0; i < s.n; i += step) {
                var c = pal((s.depth[i] - rg[0]) / (rg[1] - rg[0]));
                L.circleMarker([s.lat[i], s.lon[i]], { pane: 'sdmLines', radius: 1.8, stroke: false, fillOpacity: 0.9, fillColor: 'rgb(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ')', interactive: false }).addTo(dots);
            }
            lyr.dots = dots.addTo(map);
        }
        if (layersOn.contours) {
            var cg = L.layerGroup(), major = res.interval * 5;
            res.contours.forEach(function (cl) {
                var isMajor = Math.abs(cl.level / major - Math.round(cl.level / major)) < 1e-6;
                cl.lines.forEach(function (a) {
                    var ll = [];
                    for (var i2 = 0; i2 < a.length; i2 += 2) ll.push([a[i2 + 1], a[i2]]);
                    L.polyline(ll, { pane: 'sdmLines', color: '#0b2350', weight: isMajor ? 2.2 : 1.1, opacity: isMajor ? 0.95 : 0.7, interactive: false }).addTo(cg);
                });
            });
            lyr.contours = cg.addTo(map);
        }
        if (layersOn.features) {
            var fg = L.layerGroup();
            var addF = function (list, kind) {
                list.forEach(function (f) {
                    var label = featureLabel(kind, f);
                    var icon = L.divIcon({ className: 'sdm-fmark sdm-f-' + kind, html: '<span aria-hidden="true"></span>', iconSize: [18, 18], iconAnchor: [9, 9] });
                    L.marker([f.lat, f.lon], { icon: icon, pane: 'markerPane', keyboard: true, title: label, alt: label }).bindTooltip(esc(label), { direction: 'top', offset: [0, -8] }).addTo(fg);
                });
            };
            addF(res.features.holes, 'hole');
            addF(res.features.humps, 'hump');
            addF(res.features.drops.slice(0, 8), 'drop');
            lyr.features = fg.addTo(map);
        }
        placeLabels();
        renderLegend();
        renderSummary();
        renderFeatures();
        if (first) map.fitBounds(L.latLngBounds([[ (R.dataBounds(res)).south, R.dataBounds(res).west ], [ R.dataBounds(res).north, R.dataBounds(res).east ]]), { padding: [24, 24], maxZoom: 19 });
        if (view === '3d') open3d();
    }

    // Isobath labels: placed on screen, one per line, never two closer than
    // 70 px — re-placed after every zoom.
    function placeLabels() {
        labelMarkers.forEach(function (m) { try { map.removeLayer(m); } catch (_) {} });
        labelMarkers = [];
        if (!map || !result || !layersOn.contours || !layersOn.labels) return;
        var placed = [], size = map.getSize(), max = 80;
        var cand = [];
        result.contours.forEach(function (cl) {
            cl.lines.forEach(function (a) {
                var n = a.length / 2;
                if (n < 4) return;
                var mid = Math.floor(n / 2);
                var p = map.latLngToContainerPoint([a[2 * mid + 1], a[2 * mid]]);
                if (p.x < 10 || p.y < 10 || p.x > size.x - 10 || p.y > size.y - 10) return;
                var p0 = map.latLngToContainerPoint([a[1], a[0]]), p1 = map.latLngToContainerPoint([a[a.length - 1], a[a.length - 2]]);
                cand.push({ lvl: cl.level, ll: [a[2 * mid + 1], a[2 * mid]], p: p, len: Math.abs(p1.x - p0.x) + Math.abs(p1.y - p0.y) + n });
            });
        });
        cand.sort(function (x, y) { return y.len - x.len; });
        for (var i = 0; i < cand.length && labelMarkers.length < max; i++) {
            var c = cand[i], ok = true;
            for (var k = 0; k < placed.length; k++) if (Math.abs(placed[k].x - c.p.x) < 70 && Math.abs(placed[k].y - c.p.y) < 26) { ok = false; break; }
            if (!ok) continue;
            placed.push(c.p);
            var m = L.marker(c.ll, { pane: 'sdmLines', interactive: false, keyboard: false, icon: L.divIcon({ className: 'sdm-clabel', html: '<span>' + esc(levelTxt(c.lvl)) + '</span>', iconSize: null }) });
            m.addTo(map);
            labelMarkers.push(m);
        }
    }

    function sampleDepth(lat, lon) {
        if (!result) return NaN;
        var g = result.geo, fc = (lon - g.lon0) / g.dLon, fr = (g.lat0 - lat) / g.dLat;
        var c = Math.floor(fc), r = Math.floor(fr);
        if (c < 0 || r < 0 || c + 1 >= g.w || r + 1 >= g.h) return NaN;
        var tx = fc - c, ty = fr - r, D = result.depth, w = g.w;
        var a = D[r * w + c], b = D[r * w + c + 1], cc = D[(r + 1) * w + c], d = D[(r + 1) * w + c + 1];
        if (a !== a || b !== b || cc !== cc || d !== d) {
            var nn = D[Math.round(fr) * w + Math.round(fc)];
            return nn;
        }
        return (a * (1 - tx) + b * tx) * (1 - ty) + (cc * (1 - tx) + d * tx) * ty;
    }
    function showTip(ll, pt, sticky) {
        if (!result) return;
        if (!tip) { tip = document.createElement('div'); tip.className = 'sdm-tip'; tip.setAttribute('role', 'status'); $('sdmStage').appendChild(tip); }
        var d = sampleDepth(ll.lat, ll.lng);
        if (!(d === d)) { tip.hidden = true; return; }
        tip.textContent = t('tip_depth', { d: depthTxt(d) });
        tip.hidden = false;
        tip.style.left = Math.round(pt.x + 14) + 'px';
        tip.style.top = Math.round(pt.y + 14) + 'px';
        if (sticky) ae('feature_use', { slug: SLUG, f: 'sdm_probe' });
    }

    function renderLegend() {
        var el = $('sdmLegend');
        if (!result || !layersOn.depth) { el.hidden = true; return; }
        var R = window.SDMRender, rg = R.range(result), pal = R.palette(palette), stops = [];
        for (var i = 0; i <= 10; i++) { var c = pal(i / 10); stops.push('rgb(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ') ' + (i * 10) + '%'); }
        el.innerHTML = '<span class="sdm-leg-t">' + esc(t('legend')) + '</span><span class="sdm-leg-bar" style="background:linear-gradient(90deg,' + stops.join(',') + ')"></span>' +
            '<span class="sdm-leg-v"><span>' + esc(depthTxt(rg[0])) + '</span><span>' + esc(depthTxt(rg[1])) + '</span></span>';
        el.hidden = false;
    }

    function renderSummary() {
        var s = result.stats, a = result.accuracy;
        var dropped = s.spike + s.falseBottom + s.jump + s.shallow;
        var html = '' +
            stat(t('s_depths'), depthTxt(Math.max(0, s.dmin)) + ' – ' + depthTxt(s.dmax), t('s_mean', { v: depthTxt(s.meanDepth) })) +
            stat(t('s_area'), areaTxt(s.area), t('s_volume', { v: volumeTxt(s.volume) })) +
            stat(t('s_track'), lengthTxt(s.trackLength), t('s_points', { n: fmtN(s.kept, 0) })) +
            stat(t('s_step'), levelTxt(result.interval), t('s_cell', { v: lengthTxt(s.cell) }));
        $('sdmStats').innerHTML = html;
        var acc = $('sdmAccuracy');
        acc.className = 'tc-msg' + (a ? (a.p90 <= 0.3 ? ' good' : a.p90 > 1 ? ' warn' : '') : '');
        acc.textContent = a ? t('acc_line', { mae: depthTxt(a.mae, 2), p90: depthTxt(a.p90, 2) }) : t('acc_none');
        var cl = $('sdmCleaned');
        cl.textContent = dropped > 0 ? t('s_cleaned', { n: fmtN(dropped, 0), spike: fmtN(s.spike + s.falseBottom, 0), jump: fmtN(s.jump, 0) }) : '';
        cl.hidden = !dropped;
        function stat(l, v, sub) {
            return '<div class="tc-stat"><div class="tc-stat-l">' + esc(l) + '</div><div class="tc-stat-v">' + esc(v) + '</div>' + (sub ? '<div class="tc-stat-s">' + esc(sub) + '</div>' : '') + '</div>';
        }
        var iv = $('sdmInterval');
        if (iv) { var o0 = iv.querySelector('option[value="0"]'); if (o0) o0.textContent = t('interval_auto', { v: levelTxt(result.interval) }); }
    }

    function featureLabel(kind, f) {
        if (kind === 'hole') return t('f_hole', { d: depthTxt(f.depth), p: depthTxt(f.prom) });
        if (kind === 'hump') return t('f_hump', { d: depthTxt(f.depth), p: depthTxt(f.prom) });
        return t('f_drop', { a: depthTxt(f.dmin), b: depthTxt(f.dmax), s: fmtN(f.slope, 0) });
    }
    function renderFeatures() {
        var F = result.features, box = $('sdmFeatures'), items = [];
        F.holes.slice(0, 8).forEach(function (f) { items.push(['hole', f]); });
        F.humps.slice(0, 8).forEach(function (f) { items.push(['hump', f]); });
        F.drops.slice(0, 8).forEach(function (f) { items.push(['drop', f]); });
        if (!items.length) { box.innerHTML = '<p class="tc-hint">' + esc(t('f_none')) + '</p>'; return; }
        box.innerHTML = items.map(function (x, i) {
            return '<button type="button" class="sdm-fitem sdm-fi-' + x[0] + '" data-i="' + i + '"><span class="sdm-fdot" aria-hidden="true"></span><span>' + esc(featureLabel(x[0], x[1])) + '</span></button>';
        }).join('');
        Array.prototype.forEach.call(box.querySelectorAll('.sdm-fitem'), function (b) {
            b.addEventListener('click', function () {
                var it = items[Number(b.getAttribute('data-i'))];
                if (view === '3d') setView('map');
                if (map) map.setView([it[1].lat, it[1].lon], Math.max(map.getZoom(), 18));
                ae('feature_use', { slug: SLUG, f: 'sdm_feature_' + it[0] });
            });
        });
    }

    // ── files list ─────────────────────────────────────────────────────────
    function renderFiles() {
        var box = $('sdmFiles');
        if (!box) return;
        box.innerHTML = files.map(function (f) {
            var st = f.status === 'ready' ? t('file_points', { n: fmtN(f.n, 0) }) + ' · ' + esc(depthTxt(f.dmin)) + '–' + esc(depthTxt(f.dmax))
                : f.status === 'error' ? esc(errShort(f.err)) : esc(t('file_reading'));
            return '<div class="sdm-file' + (f.status === 'error' ? ' bad' : '') + '" data-id="' + f.id + '">' +
                '<div class="sdm-file-h"><label class="sdm-file-on"><input type="checkbox" ' + (f.enabled ? 'checked ' : '') + (f.status !== 'ready' ? 'disabled ' : '') + 'data-act="on"> <span class="sdm-file-n" title="' + esc(f.name) + '">' + esc(f.name) + '</span></label>' +
                '<button type="button" class="sdm-x" data-act="rm" aria-label="' + esc(t('file_remove', { name: f.name })) + '" title="' + esc(t('file_remove', { name: f.name })) + '">×</button></div>' +
                '<div class="sdm-file-m">' + (f.format ? '<b>' + esc(fmtLabel(f)) + '</b> · ' : '') + st + '</div>' +
                (f.status === 'ready' ? '<label class="sdm-file-off">' + esc(t('file_level')) + ' <input class="tc-in sdm-num" type="number" step="0.05" inputmode="decimal" value="' + (f.offset ? String(units === 'ft' ? Math.round(f.offset / 0.3048 * 100) / 100 : f.offset) : '0') + '" data-act="off" aria-label="' + esc(t('file_level')) + '"></label>' : '') +
                '</div>';
        }).join('');
        Array.prototype.forEach.call(box.querySelectorAll('[data-act]'), function (el) {
            var id = el.closest('.sdm-file').getAttribute('data-id'), f = files.filter(function (x) { return x.id === id; })[0];
            if (!f) return;
            var act = el.getAttribute('data-act');
            if (act === 'rm') el.addEventListener('click', function () { removeFile(id); });
            else if (act === 'on') el.addEventListener('change', function () { f.enabled = el.checked; scheduleBuild(); });
            else if (act === 'off') el.addEventListener('change', function () {
                var v = Number(String(el.value).replace(',', '.'));
                f.offset = isFinite(v) ? (units === 'ft' ? v * 0.3048 : v) : 0;
                ae('feature_use', { slug: SLUG, f: 'sdm_level' });
                scheduleBuild();
            });
        });
        $('sdmAdd').disabled = false;
    }
    function errShort(code) { var k = 'errs_' + code; return STR[k] ? t(k) : t('errs_read_failed'); }

    // ── busy / errors ──────────────────────────────────────────────────────
    function setBusy(on, text, frac) {
        var b = $('sdmBusy');
        if (!b) return;
        b.hidden = !on;
        if (!on) return;
        $('sdmBusyText').textContent = text || '';
        var bar = $('sdmBusyBar');
        if (frac == null) { bar.parentNode.classList.add('ind'); }
        else { bar.parentNode.classList.remove('ind'); bar.style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%'; }
    }
    function showErrors(list) {
        var box = $('sdmErrors');
        box.innerHTML = list.map(function (s) { return '<p>' + esc(s) + '</p>'; }).join('');
        box.hidden = false;
    }
    function hideErrors() { var box = $('sdmErrors'); box.hidden = true; box.innerHTML = ''; }
    function showWork() { $('sdmWork').hidden = false; initMap(); setTimeout(function () { if (map) map.invalidateSize(); }, 30); }

    // ── 3D ─────────────────────────────────────────────────────────────────
    var view3d = null;
    function setView(v) {
        view = v === '3d' ? '3d' : 'map';
        Array.prototype.forEach.call(document.querySelectorAll('#sdmView .tc-seg-b'), function (b) { var on = b.getAttribute('data-v') === view; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        $('sdmMap').hidden = view !== 'map';
        $('sdm3d').hidden = view !== '3d';
        $('sdmExagWrap').hidden = view !== '3d';
        if (view === '3d') open3d(); else if (map) setTimeout(function () { map.invalidateSize(); }, 20);
        ae('feature_use', { slug: SLUG, f: 'sdm_view_' + view });
    }
    function open3d() {
        if (!result) return;
        var host = $('sdm3d');
        var three = window.__vendorScript ? window.__vendorScript(CFG.threeUrl, { expect: 'THREE', label: 'three' }) : Promise.reject(new Error('no_vendor'));
        three.then(function () {
            return window.__vendorScript(CFG.orbitUrl, { expect: function () { return window.THREE && window.THREE.OrbitControls; }, label: 'three-orbit' });
        }).then(function () {
            if (view !== '3d') return;
            if (view3d) view3d.update(result, { palette: palette, exaggeration: exag });
            else view3d = window.SDM3D.open(host, result, { palette: palette, exaggeration: exag });
        }).catch(function (e) {
            ae('tool_diagnostic', { issue: 'sdm_3d_failed', err: String(e && e.message || e).slice(0, 80) });
            toast(t('three_failed'), 'warning');
            setView('map');
        });
    }

    // ── export ─────────────────────────────────────────────────────────────
    var FORMATS = {
        png: { ext: 'png', mime: 'image/png' }, pdf: { ext: 'pdf', mime: 'application/pdf' },
        gpx: { ext: 'gpx', mime: 'application/gpx+xml' }, kml: { ext: 'kml', mime: 'application/vnd.google-earth.kml+xml' },
        kmz: { ext: 'kmz', mime: 'application/vnd.google-earth.kmz' }, csv: { ext: 'csv', mime: 'text/csv' },
        tif: { ext: 'tif', mime: 'image/tiff' }
    };
    function baseName() {
        var v = ($('sdmName').value || '').trim();
        if (!v) v = files.length ? files[0].name.replace(/\.[^.]+$/, '') + '-depth' : 'depth-map';
        try { if (window.__outputName && window.__outputName.sanitize) v = window.__outputName.sanitize(v); } catch (_) {}
        return v || 'depth-map';
    }
    function mapKey() {
        var keys = files.filter(function (f) { return f.status === 'ready' && f.enabled; }).map(function (f) { return f.key; }).sort();
        return 'set:' + keys.join('+');
    }
    function exportOpts() {
        return {
            name: baseName(), brand: CFG.brand,
            label: function (l) { return levelTxt(l); },
            feature: featureLabel,
            color: function (l) { var R = window.SDMRender, rg = R.range(result); return R.palette(palette)((l - rg[0]) / (rg[1] - rg[0])); },
            contoursName: t('contours_name'), featuresName: t('features_name'), overlayName: t('legend')
        };
    }
    function composeOpts() {
        var s = result.stats, a = result.accuracy, names = files.filter(function (f) { return f.status === 'ready' && f.enabled; }).map(function (f) { return f.name; });
        var date = '';
        var st = files.map(function (f) { return f.meta && f.meta.startTime; }).filter(Boolean)[0];
        try { if (st) date = new Intl.DateTimeFormat(intlLocale(), { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(st)); } catch (_) {}
        return {
            palette: palette, units: units, fmtDepth: levelTxt, fmtLegend: function (m) { return depthTxt(m); }, fmtLength: lengthTxt,
            title: baseName(), subtitle: names.slice(0, 3).join(', ') + (names.length > 3 ? ' +' + (names.length - 3) : '') + (date ? ' · ' + date : ''),
            legendTitle: t('legend'),
            footer: t('img_footer', { n: fmtN(s.kept, 0), step: levelTxt(result.interval) }) + (a ? ' · ' + t('img_acc', { mae: depthTxt(a.mae, 2) }) : '') + (sampleMode ? ' · ' + t('sample_short') : ''),
            brand: CFG.brand, featureLabel: featureLabel,
            layers: { depth: true, contours: true, features: layersOn.features },
            basemap: !sampleMode
        };
    }
    function makeBlob(fmt) {
        var E = window.SDMExport, R = window.SDMRender;
        if (fmt === 'gpx') return Promise.resolve(new Blob([E.gpx(result, exportOpts())], { type: FORMATS.gpx.mime }));
        if (fmt === 'kml') return Promise.resolve(new Blob([E.kml(result, exportOpts())], { type: FORMATS.kml.mime }));
        if (fmt === 'csv') return Promise.resolve(new Blob(['\uFEFF' + E.csv(result, { header: t('csv_header') })], { type: FORMATS.csv.mime }));
        if (fmt === 'tif') return Promise.resolve(new Blob([E.geotiff(result)], { type: FORMATS.tif.mime }));
        if (fmt === 'kmz') {
            return vendor(CFG.fflateUrl, 'fflate').then(function () {
                return toBytes(R.overlay(result, { palette: palette, alpha: 255 }), 'image/png');
            }).then(function (png) {
                var o = exportOpts(); o.overlay = 'depth.png';
                var kml = E.kml(result, o);
                var zip = window.fflate.zipSync({ 'doc.kml': new TextEncoder().encode(kml), 'depth.png': [png, { level: 0 }] }, { level: 6 });
                return new Blob([zip], { type: FORMATS.kmz.mime });
            });
        }
        if (fmt === 'png' || fmt === 'pdf') {
            return R.compose(result, composeOpts()).then(function (cv) {
                if (fmt === 'png') return canvasBlob(cv, 'image/png');
                return vendor(CFG.jspdfUrl, 'jspdf').then(function () {
                    var J = window.jspdf && window.jspdf.jsPDF;
                    var land = cv.width >= cv.height;
                    var pdf = new J({ orientation: land ? 'landscape' : 'portrait', unit: 'mm', format: 'a4', compress: true });
                    var pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight(), m = 8;
                    var sc = Math.min((pw - 2 * m) / cv.width, (ph - 2 * m) / cv.height);
                    return canvasDataUrl(cv).then(function (url) {
                        pdf.addImage(url, 'JPEG', (pw - cv.width * sc) / 2, (ph - cv.height * sc) / 2, cv.width * sc, cv.height * sc);
                        return { pdf: pdf };
                    });
                });
            });
        }
        return Promise.reject(new Error('fmt'));
    }
    function vendor(url, expect) {
        if (window[expect]) return Promise.resolve();
        if (window.__vendorScript) return window.__vendorScript(url, { expect: expect, label: expect });
        return new Promise(function (res, rej) { var s = document.createElement('script'); s.src = url; s.onload = res; s.onerror = function () { rej(new Error(expect)); }; document.head.appendChild(s); });
    }
    // timbrica-core.js owns canvas encoding (toBlob → OffscreenCanvas → data URL).
    function canvasBlob(cv, type, q) {
        if (window.__canvasBlob) return window.__canvasBlob(cv, type, q);
        return Promise.reject(new Error('CANVAS_ENCODE_FAILED'));
    }
    function canvasDataUrl(cv) {
        return canvasBlob(cv, 'image/jpeg', 0.92).then(function (b) {
            return new Promise(function (res) { var r = new FileReader(); r.onload = function () { res(r.result); }; r.readAsDataURL(b); });
        });
    }
    function toBytes(cv, type) { return canvasBlob(cv, type).then(function (b) { return b.arrayBuffer(); }).then(function (ab) { return new Uint8Array(ab); }); }

    function download() {
        if (!result) return;
        var fmt = $('sdmFmt').value, F = FORMATS[fmt];
        if (!F) return;
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [mapKey()]) : { commit: function () {}, abort: function () {} };
        if (!run) return;
        var name = baseName() + '.' + F.ext;
        var btn = $('sdmSave');
        btn.disabled = true;
        save('fmt', fmt);
        makeBlob(fmt).then(function (out) {
            if (out && out.pdf) {
                if (window.__savePdf) return window.__savePdf(out.pdf, name);
                return window.__downloadFile(out.pdf.output('blob'), name);
            }
            return window.__downloadFile(out, name);
        }).then(function (ok) {
            btn.disabled = false;
            if (ok === false) { run.abort(); return; }
            run.commit();
            try { if (window.__aeToolComplete) window.__aeToolComplete({ fmt: fmt, files: files.length, bins: result.stats.bins, sample: sampleMode ? 1 : 0 }); } catch (_) {}
        }, function (e) {
            btn.disabled = false;
            run.abort();
            if (window.__handleError) window.__handleError(e, 'sdm_export_' + fmt, { say: t('save_failed') });
            else toast(t('save_failed'), 'error');
        });
    }

    // ── sample ─────────────────────────────────────────────────────────────
    function openSample() {
        if (!window.SDMDemo) return;
        warmWorker();
        var demo = window.SDMDemo.sl2({});
        var file = new File([demo.bytes], 'model-lake.sl2', { type: 'application/octet-stream', lastModified: 1 });
        var zone = $('sdmUpload');
        sampleWanted = true;
        ae('feature_use', { slug: SLUG, f: 'sdm_sample' });
        if (zone && typeof zone.__uploaderInjectFile === 'function') zone.__uploaderInjectFile(file, 'sample');
        else { sampleWanted = false; addFiles([file], 'replace', true); }
    }

    // ── controls ───────────────────────────────────────────────────────────
    function syncLayerInputs() {
        Object.keys(layersOn).forEach(function (k) { var el = $('sdmL_' + k); if (el) el.checked = !!layersOn[k]; });
        var lab = $('sdmL_labels'); if (lab) lab.disabled = !layersOn.contours;
    }
    function bindControls() {
        var zone = $('sdmUpload');
        var addMode = false;
        var onFiles = function (list) {
            var isSample = sampleWanted && list.length === 1 && list[0] && list[0].name === 'model-lake.sl2';
            sampleWanted = false;
            hideErrors();
            addFiles(list, addMode ? 'add' : 'replace', isSample);
            addMode = false;
            applySat();
        };
        if (zone) {
            if (window.__bindUploaderFile) window.__bindUploaderFile(zone, function (f, d) { onFiles(d && d.files && d.files.length ? d.files : [f]); });
            else zone.addEventListener('tool-uploader:file', function (e) { var d = e.detail || {}; onFiles(d.files || [d.file]); });
            zone.addEventListener('tool-uploader:clear', function () { clearAll(true); });
            ['pointerenter', 'dragenter', 'focusin', 'touchstart'].forEach(function (ev) { zone.addEventListener(ev, warmWorker, { once: true, passive: true }); });
        }
        $('sdmAdd').addEventListener('click', function () {
            var inp = document.querySelector('[data-uploader-for=sdmUpload]') || (zone && zone.querySelector('input[type=file]'));
            if (!inp) return;
            addMode = true;
            inp.click();
        });
        $('sdmSample').addEventListener('click', openSample);

        var iv = $('sdmInterval');
        iv.value = String(opts.interval || 0);
        if (iv.value !== String(opts.interval || 0)) iv.value = '0';
        iv.addEventListener('change', function () { opts.interval = Number(iv.value) || 0; save('interval', opts.interval); ae('feature_use', { slug: SLUG, f: 'sdm_interval', v: opts.interval }); scheduleBuild(); });
        segBind('sdmSmooth', String(opts.smooth), function (v) { opts.smooth = Number(v); save('smooth', opts.smooth); scheduleBuild(); });
        var gp = $('sdmGaps');
        gp.value = String(opts.maxEdge);
        if (gp.value !== String(opts.maxEdge)) gp.value = '60';
        gp.addEventListener('change', function () { opts.maxEdge = Number(gp.value); save('gaps', opts.maxEdge); ae('feature_use', { slug: SLUG, f: 'sdm_gaps', v: opts.maxEdge }); scheduleBuild(); });
        var td = $('sdmTransducer');
        td.value = opts.transducer ? String(units === 'ft' ? Math.round(opts.transducer / 0.3048 * 100) / 100 : opts.transducer) : '0';
        td.addEventListener('change', function () {
            var v = Number(String(td.value).replace(',', '.'));
            opts.transducer = isFinite(v) ? (units === 'ft' ? v * 0.3048 : v) : 0;
            save('transducer', opts.transducer);
            scheduleBuild();
        });
        segBind('sdmPalette', palette, function (v) { palette = v; save('palette', v); if (result) render(false); if (view3d && result) view3d.update(result, { palette: palette, exaggeration: exag }); });
        segBind('sdmUnits', units, function (v) {
            units = v; save('units', v);
            renderFiles();
            if (result) { renderSummary(); renderFeatures(); renderLegend(); placeLabels(); }
            // isobaths are drawn at round numbers of the unit on screen: 2/5/10 ft, not 3.28 ft
            if (files.some(function (f) { return f.status === 'ready'; })) scheduleBuild(0);
            td.value = opts.transducer ? String(units === 'ft' ? Math.round(opts.transducer / 0.3048 * 100) / 100 : Math.round(opts.transducer * 100) / 100) : '0';
            relabelUnits();
        });
        relabelUnits();
        segBind('sdmView', 'map', function (v) { setView(v); });
        var ex = $('sdmExag'), exv = $('sdmExagV');
        ex.value = String(exag); exv.textContent = '×' + exag;
        ex.addEventListener('input', function () { exag = Number(ex.value) || 6; exv.textContent = '×' + exag; save('exag', exag); if (view3d) view3d.setExaggeration(exag); });
        Object.keys(layersOn).forEach(function (k) {
            var el = $('sdmL_' + k);
            if (!el) return;
            el.addEventListener('change', function () {
                layersOn[k] = el.checked; save('l_' + k, el.checked);
                ae('feature_use', { slug: SLUG, f: 'sdm_layer_' + k, on: el.checked ? 1 : 0 });
                if (k === 'sat') applySat();
                else if (result) render(false);
                syncLayerInputs();
            });
        });
        syncLayerInputs();
        var fmt = $('sdmFmt'), fsaved = load('fmt', 'png');
        if (FORMATS[fsaved]) fmt.value = fsaved;
        $('sdmSave').addEventListener('click', download);
        var full = $('sdmFull');
        if (full && window.__wholeScreen) {
            var ws = window.__wholeScreen($('sdmApp'), {
                key: 'sdm', onChange: function (on) {
                    full.setAttribute('aria-pressed', on ? 'true' : 'false');
                    full.querySelector('.sdm-full-on').hidden = on; full.querySelector('.sdm-full-off').hidden = !on;
                    setTimeout(function () { if (map) map.invalidateSize(); if (view3d) view3d.resize(); }, 60);
                }
            });
            full.addEventListener('click', function () { ws.toggle(); });
        } else if (full) full.hidden = true;
    }
    function relabelUnits() {
        Array.prototype.forEach.call(document.querySelectorAll('[data-sdm-unit]'), function (el) { el.textContent = t(units === 'ft' ? 'u_ft' : 'u_m'); });
    }
    function segBind(id, cur, onPick) {
        var box = $(id);
        if (!box) return;
        Array.prototype.forEach.call(box.querySelectorAll('.tc-seg-b'), function (b) {
            var on = b.getAttribute('data-v') === String(cur);
            b.classList.toggle('on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
            b.addEventListener('click', function () {
                var v = b.getAttribute('data-v');
                Array.prototype.forEach.call(box.querySelectorAll('.tc-seg-b'), function (x) { var o2 = x === b; x.classList.toggle('on', o2); x.setAttribute('aria-pressed', o2 ? 'true' : 'false'); });
                onPick(v);
            });
        });
    }

    function boot() {
        if (!$('sdmApp')) return;
        bindControls();
        stage('page_ready');
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
